if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.gallerySlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        this.enablePointerDrag();
        this.enableSnapCorrection();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 150));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      enablePointerDrag() {
        if (!this.gallerySlider) return;

        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let moved = false;

        this.gallerySlider.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = this.gallerySlider.scrollLeft;
          moved = false;
          this.gallerySlider.setPointerCapture(pointerId);
        });

        this.gallerySlider.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;
          const deltaX = event.clientX - startX;
          if (!moved && Math.abs(deltaX) < 5) return;
          moved = true;
          this.gallerySlider.classList.add('is-pointer-dragging');
          this.gallerySlider.scrollLeft = startScrollLeft - deltaX;
          event.preventDefault();
        });

        const finishDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          if (this.gallerySlider.hasPointerCapture(pointerId)) this.gallerySlider.releasePointerCapture(pointerId);
          pointerId = null;
          this.gallerySlider.classList.remove('is-pointer-dragging');
          if (!moved) return;

          this.suppressGalleryClick = true;
          const activeSlide = this.getNearestGallerySlide();
          if (activeSlide) this.gallerySlider.scrollTo({
            left: activeSlide.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
          window.setTimeout(() => { this.suppressGalleryClick = false; }, 0);
        };

        this.gallerySlider.addEventListener('pointerup', finishDrag);
        this.gallerySlider.addEventListener('pointercancel', finishDrag);
        this.gallerySlider.addEventListener('click', (event) => {
          if (!this.suppressGalleryClick) return;
          event.preventDefault();
          event.stopPropagation();
        }, true);
      }

      getNearestGallerySlide() {
        if (!this.gallerySlider) return null;
        const slides = this.getGallerySlides();
        if (!slides.length) return null;
        return slides.reduce((nearest, slide) => (
          Math.abs(slide.offsetLeft - this.gallerySlider.scrollLeft) <
          Math.abs(nearest.offsetLeft - this.gallerySlider.scrollLeft) ? slide : nearest
        ));
      }

      getGallerySlides() {
        if (!this.gallerySlider) return [];
        return Array.from(this.gallerySlider.children).filter((element) =>
          element.matches('.product__media-item[data-media-id]')
        );
      }

      snapGalleryToNearest() {
        if (!this.gallerySlider || this.gallerySlider.classList.contains('is-pointer-dragging')) return;
        const activeSlide = this.getNearestGallerySlide();
        if (!activeSlide || Math.abs(activeSlide.offsetLeft - this.gallerySlider.scrollLeft) < 1) return;
        this.gallerySlider.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
      }

      enableSnapCorrection() {
        if (!this.gallerySlider) return;
        if ('onscrollend' in this.gallerySlider) {
          this.gallerySlider.addEventListener('scrollend', () => this.snapGalleryToNearest(), { passive: true });
          return;
        }
        let scrollTimer;
        this.gallerySlider.addEventListener('scroll', () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => this.snapGalleryToNearest(), 140);
        }, { passive: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const preventPageScroll = this.dataset.dtPreventPageScroll === 'true';
        delete this.dataset.dtPreventPageScroll;
        const gallerySlides = this.getGallerySlides();
        const activeMedia =
          gallerySlides.find((element) => element.dataset.mediaId === mediaId) ||
          gallerySlides[0];
        if (!activeMedia) {
          return;
        }
        gallerySlides.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!preventPageScroll) {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const slider = this.elements.thumbnails.slider;
        if (!slider) return;
        const centeredLeft = thumbnail.offsetLeft - (slider.clientWidth - thumbnail.offsetWidth) / 2;
        const maxLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
        slider.scrollTo({
          left: Math.max(0, Math.min(maxLeft, centeredLeft)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
